import React from 'react';
import { differenceInMinutes } from 'date-fns';

import { IconInfoCircle, LoadingEllipses } from '@trqk/components';
import { locale } from '@trqk/utils';

/**
 * @typedef {RefreshStatusProps}
 * @property {EntityRefreshStatus} entityRefreshStatus the entityRefreshStatus for the current
 * entity
 */
interface RefreshStatusProps {
    entityRefreshStatus: EntityRefreshStatus;
}

/**
 * Shows a message based on the state of the last update to the list of tracks. If a statement was
 * recently ingested or deleted, but the track list has not been updated yet, the user will see
 * some interim messaging. If the tracks list was updated in the last five minutes, the user will
 * see a message to that effect. The intent is to alert the user to the fact that there is some
 * processing time between statement ingestion / deletion and the tracks list being updated.
 * @param {RefreshStatusProps} props
 */
export const RefreshStatus = ({
    entityRefreshStatus,
}: RefreshStatusProps): JSX.Element | null => {
    const { isDeletingStatement, isIngestingStatement, tracksFetchedAt } =
        entityRefreshStatus;

    const tracksFetchedInLastFiveMinutes =
        differenceInMinutes(new Date(), new Date(tracksFetchedAt)) < 5;

    if (
        !isIngestingStatement &&
        !isDeletingStatement &&
        !tracksFetchedInLastFiveMinutes
    ) {
        return null;
    }

    return (
        <div className="flex items-start justify-start text-sm text-tertiary-400 italic mx-2">
            {isIngestingStatement && (
                <div className="align-middle">
                    <LoadingEllipses
                        width={26}
                        fillColor="tertiary-400"
                        addClasses="mr-2"
                    />
                    {locale.enUS.WORKS.REFRESH_STATUS.CHECKING_FOR_NEW_WORKS}
                </div>
            )}
            {!isIngestingStatement && isDeletingStatement && (
                <div className="align-middle">
                    <LoadingEllipses
                        width={26}
                        fillColor="tertiary-400"
                        addClasses="mr-2"
                    />
                    {locale.enUS.WORKS.REFRESH_STATUS.WORKS_BEING_CLEANED_UP}
                </div>
            )}
            {!isIngestingStatement &&
                !isDeletingStatement &&
                tracksFetchedInLastFiveMinutes && (
                    <>
                        <IconInfoCircle
                            fillColor="primary-500"
                            addClasses="mr-1"
                            width={20}
                        />
                        {locale.enUS.WORKS.REFRESH_STATUS.WORK_LIST_UPDATED}
                    </>
                )}
        </div>
    );
};
